import { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'

const AuthContext = createContext(null)

// ✅ Always use the absolute API URL — relative paths break in production
//    (no Vite proxy exists on the live server)
const API = import.meta.env.VITE_API_URL || ''

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Skip the refresh call entirely when there is no session cookie.
    // This prevents an unnecessary 401 in the console for unauthenticated visitors.
    if (!localStorage.getItem('e4f_session')) {
      setLoading(false)
      return
    }

    axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, timeout: 5000 })
      .then(({ data }) => {
        window.__accessToken = data.accessToken
        setUser(data.user)
      })
      .catch(() => {
        // Refresh cookie expired or invalid — clear the session flag so we
        // don't keep firing refresh calls on future page loads.
        localStorage.removeItem('e4f_session')
        if (!window.__accessToken) setUser(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true })
    localStorage.removeItem('e4f_session')
    window.__accessToken = null
    setUser(null)
  }

  const hasFeature = (key) => {
    if (!user) return false
    if (user.role === 'super_admin') return true
    try {
      const payload = JSON.parse(atob(window.__accessToken.split('.')[1]))
      const val = payload.features?.[key]
      return val === true || val === 'true' || (typeof val === 'number' && val > 0)
    } catch (err) {
      console.warn('[auth] hasFeature: JWT decode failed —', err.message)
      return false
    }
  }

  const hasRole = (...roles) => {
    if (!user) return false
    const hierarchy = { super_admin:5, org_admin:4, supervisor:3, inspector:2, viewer:1 }
    const userLevel = hierarchy[user.role] || 0
    const required  = Math.max(...roles.map(r => hierarchy[r] || 0))
    return userLevel >= required
  }

  return (
    <AuthContext.Provider value={{ user, loading, logout, hasFeature, hasRole, setUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
